import * as Yup from 'yup';
import { ILenderUtility } from '../../interfaces';

type Omitted =
  | 'numericId'
  | 'assetTypes'
  | 'employments'
  | 'docFeeStateExclusions'
  | 'states'
  | 'dealFees'
  | 'statesAndZips'
  | 'stateSpecificEltCodes'
  | 'cards'
  | 'productTypeExclusionRules'
  | 'lenderCreditRules'
  | 'formulas';

export type TLenderUtilityLoanInfoForm = Omit<ILenderUtility, Omitted>;

export const lenderUtilityLoanInfoFormSchema = Yup.object().shape({
  code: Yup.string().required().nullable(),
  rank: Yup.string().nullable(),
  subRank: Yup.string().nullable(),
  nameOnDocs: Yup.string().nullable().required(),
  contactName: Yup.string().nullable(),
  contactPhone: Yup.string().phone().nullable(),
  ltvType: Yup.string().required().nullable(),
  openLendingConfiguration: Yup.object().when('submissionMethod.type', {
    is: 'isOpenLending',
    then: Yup.object({
      userName: Yup.string().required().nullable(),
      password: Yup.string().required().nullable(),
      institutionId: Yup.string().required().nullable(),
      endpointUrl: Yup.string().required().nullable(),
    }),
  }),
  meridianConfiguration: Yup.object().when('submissionMethod.type', {
    is: 'isMeridian',
    then: Yup.object({
      meridianApiUserId: Yup.string().required().nullable(),
      meridianUrl: Yup.string().required().nullable(),
      meridianApiPassword: Yup.string().required().nullable(),
      meridianSubmitDocUrl: Yup.string().required().nullable(),
    }),
  }),
  cudlConfiguration: Yup.object().when('submissionMethod.type', {
    is: 'isOrigence',
    then: Yup.object({
      code: Yup.string().nullable().required('Code is required'),
      userId: Yup.string().nullable().required('User ID is required'),
      password: Yup.string().nullable().required('Password is required'),
      organizationCode: Yup.string().nullable(),
      url: Yup.string().nullable().required('URL is required'),
      decisionAuthKey: Yup.string().nullable().required('Decision Auth Key is required'),
    }),
  }),
  dealerTrackId: Yup.string().nullable().when('submissionMethod.type', {
    is: 'isDealerTrack',
    then: Yup.string().required().nullable(),
    otherwise: Yup.string().nullable(),
  }),
  useBothLtv: Yup.boolean().nullable(),
  autoSubmitLoanApplication: Yup.string().nullable(),
  isFlStampFee: Yup.boolean().nullable(),
  isMeridian: Yup.boolean().nullable(),
  isOpenLending: Yup.boolean().nullable(),
  isDealertrack: Yup.boolean().nullable(),
  isQuilo: Yup.boolean().nullable(),
  isCudl: Yup.boolean().nullable(),
  isOrigence: Yup.boolean().nullable(),
  isSubprime: Yup.boolean().nullable(),
  acceptsMla: Yup.boolean().nullable(),
  cuCollaborateCDFICheck: Yup.boolean().nullable(),
  isCUCollaborateOnly: Yup.boolean().nullable(),
  submitNakedDeal: Yup.boolean().nullable(),
  submissionMethod: Yup.object({
    type: Yup.string()
      .nullable()
      .oneOf(['isOpenLending', 'isMeridian', 'isDealerTrack', 'isQuilo', 'isCudl', 'isSubprime', 'isOrigence', null]),
  }).nullable(),
  generalInfo: Yup.object({
    fundingFeeGreen: Yup.number().nullable(),
    maxFlatGreen: Yup.number().nullable(),
    maxFlatPercentGreen: Yup.number().nullable(),
    allowEmployerZip: Yup.boolean().nullable(),
    defaultValuationMethods: Yup.array().of(Yup.string()).min(1, 'Please select default valuation methods').required(),
    daysToFirstPaymentGreen: Yup.number().nullable(),
    firstPaymentDates: Yup.date().nullable(),
    maxDtiGreen: Yup.number().nullable(),
    maxPtiGreen: Yup.number().nullable(),
    maxMileageGreen: Yup.number().required('Is Required'),
    maxAgeGreen: Yup.number().required('Is Required').max(255, 'Incorrect value'),
    maxCashOutGreen: Yup.number().nullable(),
    minPrimaryFicoGreen: Yup.number().required('Is Required').nullable(),
    minSecondaryFicoGreen: Yup.number().nullable(),
    availableMarkupIncrements: Yup.string().nullable(),
    achDiscount: Yup.number().nullable(),
    gapLtvLimit: Yup.number().min(0, 'Should be more than zero').nullable(),
    maxProduct: Yup.number().nullable(),
    maxOtherProducts: Yup.number().nullable(),
    preferredScore: Yup.string().required().nullable(),
    creditReport: Yup.string().nullable(),
    approvalDaysGreen: Yup.number().nullable(),
    daysToFirstPaymentYellow: Yup.number().nullable(),
    maxDtiYellow: Yup.number().nullable(),
    maxPtiYellow: Yup.number().nullable(),
    maxMileageYellow: Yup.number().nullable(),
    maxAgeYellow: Yup.number().max(255).nullable(),
    minPrimaryFicoYellow: Yup.number().nullable(),
    minSecondaryFicoYellow: Yup.number().nullable(),
    approvalDaysYellow: Yup.number().nullable(),
    maxCashOutYellow: Yup.number().nullable(),
    fundingFeeYellow: Yup.number().nullable(),
    useAprBasedCalculation: Yup.boolean().nullable(),
  }),
  productCheckRequestPayee: Yup.object({
    id: Yup.string(),
    lenderId: Yup.string(),
    lenderName: Yup.string(),
    numericId: Yup.number(),
    payeeName: Yup.string().nullable(),
    payeeAddress: Yup.string().nullable(),
    payeeState: Yup.string().nullable(),
    payeeZip: Yup.number().nullable(),
  }),
});

export const stateRulesValidation = Yup.object().shape({
  state: Yup.string().required(),
  zips: Yup.string().nullable(),
  bureau: Yup.string().nullable(),
  daysToFirstPayment: Yup.number().min(0).integer().nullable(),
  loanAmount: Yup.number().min(0).nullable(),
  loanAmountOperator: Yup.string().when('loanAmount', {
    is: (loanAmount: number) => !!loanAmount,
    then: Yup.string().required().nullable(),
    otherwise: Yup.string().nullable(),
  }),
  apr: Yup.number().nullable(),
  aprOperator: Yup.string().when('apr', {
    is: (apr: number) => !!apr,
    then: Yup.string().required().nullable(),
    otherwise: Yup.string().nullable(),
  }),
  rate: Yup.number().nullable(),
  rateOperator: Yup.string().when('rate', {
    is: (rate: number) => !!rate,
    then: Yup.string().required().nullable(),
    otherwise: Yup.string().nullable(),
  }),
});
